import React from "react";

import { Category, DirectionsCar, Person } from "@mui/icons-material";
import { Avatar, Box, Typography } from "@mui/material";
import { HighlightText } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { IEvidenceFile } from "@/types/typesAlerts";

import { entityPhotos } from "./entityPhotos";
import { BulletinEntityResource, EntityRow, toEntityRows } from "./entityRows";

/**
 * The people, vehicles and items on a published bulletin.
 *
 * One full-width row each, split in two: who or what it is, the name it is known by and the photos
 * it appears in on the left; the values the record stores and the officer's own description on the
 * right. This is what the old free-text tag block became — a bulletin holding structured records
 * can say "Suspect #1 - John Smith" where before it said whatever the officer typed.
 *
 * Independent of the creation flows on purpose. `DisplayAlert` renders it today; the board card and
 * the generated PDF are the next two callers, and neither of those belongs to `add/` or `add-v2/`
 * either. Everything it needs arrives as props — no context, no hooks beyond translation.
 */

const ICONS: Record<EntityRow["type"], React.ReactElement> = {
  person: <Person sx={{ fontSize: 17 }} />,
  vehicle: <DirectionsCar sx={{ fontSize: 17 }} />,
  item: <Category sx={{ fontSize: 17 }} />,
};

/**
 * The inline size at which the row splits in two.
 *
 * A container query, not a breakpoint: the same list is read at the page's 710px and in a drawer
 * the reader drags to any width between 200px and 80vw, half of it again when two bulletins are
 * compared side by side. A viewport breakpoint knows none of that. The number is what the split
 * needs — a 220px identity column and enough line beside it to describe a suspect.
 */
const SPLIT_WIDTH = "480px";

const labelSx = {
  flex: "0 0 66px",
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  lineHeight: 1.5,
} as const;

const chipSx = {
  display: "inline-flex",
  alignItems: "center",
  height: 24,
  px: "7px",
  borderRadius: 1,
  bgcolor: "action.selected",
  fontSize: "0.875rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minHeight: 24 }}
  >
    <Typography variant="caption" sx={{ ...labelSx, pt: "3px" }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {children}
    </Box>
  </Box>
);

interface BulletinEntityListProps {
  entities: BulletinEntityResource[];
  /** The bulletin's media, in the order the gallery shows it, so a photo can be named by its letter. */
  medias?: IEvidenceFile[];
  /** What a photo letter means. The detail view puts that photo on the gallery's hero. */
  onPhotoClick?: (index: number) => void;
  /** The board's search text, so a hit inside an entity is highlighted as it is everywhere else. */
  searchText?: string | null;
}

const NO_MEDIA: IEvidenceFile[] = [];

export const BulletinEntityList: React.FC<BulletinEntityListProps> = ({
  entities,
  medias = NO_MEDIA,
  onPhotoClick,
  searchText,
}) => {
  const { t } = useTranslation("bulletin/entities");
  const rows = toEntityRows(entities, t);
  const photos = entityPhotos(medias);

  if (rows.length === 0) return null;

  return (
    <Box sx={{ containerType: "inline-size" }}>
      {rows.map((row) => {
        const shots = photos[row.id] ?? [];

        return (
          <Box
            key={row.id}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 2.5,
              px: 1,
              py: 1.5,
              mx: -1,
              borderRadius: 1,
              borderBottom: "1px dotted",
              borderColor: "divider",
              // Nothing follows the last row, so nothing is being separated from anything.
              "&:last-of-type": { borderBottom: 0 },
              [`@container (min-width: ${SPLIT_WIDTH})`]: {
                gridTemplateColumns: "220px minmax(0, 1fr)",
              },
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              {/*
                Neutral, not the theme's primary: an entity row is not a status, and a column of
                filled blue circles reads as a row of buttons.
              */}
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  mt: "-3px",
                  bgcolor: "action.selected",
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              >
                {ICONS[row.type]}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={700}>
                  <HighlightText text={row.heading} searchText={searchText} />
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    mt: 0.75,
                  }}
                >
                  <FieldRow label={row.kind}>
                    {row.name && (
                      <Box sx={chipSx}>
                        <HighlightText
                          text={row.name}
                          searchText={searchText}
                        />
                      </Box>
                    )}
                  </FieldRow>

                  {row.dateOfBirth && (
                    <FieldRow label={t("dob")}>
                      <Box sx={chipSx}>
                        <HighlightText
                          text={row.dateOfBirth}
                          searchText={searchText}
                        />
                      </Box>
                    </FieldRow>
                  )}

                  {shots.length > 0 && (
                    <FieldRow label={t(shots.length > 1 ? "photos" : "photo")}>
                      {shots.map((shot) => (
                        <Box
                          key={shot.index}
                          onClick={() => onPhotoClick?.(shot.index)}
                          sx={{
                            ...chipSx,
                            justifyContent: "center",
                            minWidth: 24,
                            cursor: onPhotoClick ? "pointer" : "default",
                            "&:hover": onPhotoClick
                              ? { bgcolor: "action.focus" }
                              : undefined,
                          }}
                        >
                          {shot.letter}
                        </Box>
                      ))}
                    </FieldRow>
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                // Stacked, the right half lines up under the identity rather than under the avatar.
                pl: "36px",
                [`@container (min-width: ${SPLIT_WIDTH})`]: { pl: 0 },
              }}
            >
              {row.fields.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: "4px 20px",
                  }}
                >
                  {row.fields.map((field) => (
                    <Box
                      key={field.label}
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ ...labelSx, flex: "0 0 86px" }}
                      >
                        {field.label}
                      </Typography>
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        <HighlightText
                          text={field.value}
                          searchText={searchText}
                        />
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {row.description && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  <HighlightText
                    text={row.description}
                    searchText={searchText}
                  />
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
